import { useState } from 'react';
import type { ReviewEntry } from '../store/gameStore';
import { REALMS } from '@wot/shared';
import { invokeFunction, supabase } from '../supabase';
import { getDeviceId } from '../lib/deviceId';

// The post-match review: every miss, with the answer and the one-line why.
// Chess.com's "analyze" moment scaled to trivia. A question that looks wrong
// to the player is one tap from the report queue.
const REASONS: { id: 'wrong_answer' | 'unclear' | 'typo'; label: string }[] = [
  { id: 'wrong_answer', label: 'Wrong answer' },
  { id: 'unclear', label: 'Unclear' },
  { id: 'typo', label: 'Typo' },
];

const LETTERS = ['A', 'B', 'C', 'D'];

export function MissedReview({ entries, matchId, nickname }: { entries: ReviewEntry[]; matchId: string | null; nickname: string }) {
  const missed = entries.filter((e) => !e.correct);
  const [open, setOpen] = useState(false);
  const [reporting, setReporting] = useState<string | null>(null);   // questionId with the picker open
  const [reported, setReported] = useState<Record<string, 'sent' | 'failed'>>({});
  if (missed.length === 0) return null;

  async function report(questionId: string, reason: string) {
    setReporting(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const res = await invokeFunction('report-question', {
        questionId, reason, matchId, nickname,
        deviceId: getDeviceId(),
        access_token: sess?.session?.access_token ?? null,
        authUserId: sess?.session?.user?.id ?? null,
      });
      setReported((r) => ({ ...r, [questionId]: res?.ok ? 'sent' : 'failed' }));
    } catch {
      setReported((r) => ({ ...r, [questionId]: 'failed' }));
    }
  }

  return (
    <div className="missed-review">
      <button className="missed-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="missed-toggle-row">
          <span className="missed-toggle-label">MISSED <b>{missed.length}</b> of {entries.length}</span>
          <span className="missed-toggle-hint">{open ? 'HIDE' : 'SEE THE ANSWERS'}</span>
        </span>
        {!open && (
          <span className="missed-toggle-realms" aria-hidden="true">
            {[...new Set(missed.map((e) => e.realm))].map((r) => {
              const realm = (REALMS as any)[r];
              return <span key={r} className="missed-toggle-realm" style={{ '--realm-color': realm?.color || '#3E7BFA' } as React.CSSProperties}>{realm?.name || r}</span>;
            })}
          </span>
        )}
      </button>
      {open && (
        <div className="missed-list">
          {missed.map((e) => {
            const realm = (REALMS as any)[e.realm];
            const state = reported[e.questionId];
            return (
              <div className="missed-item" key={e.questionId} style={{ '--realm-color': realm?.color || '#3E7BFA' } as React.CSSProperties}>
                <div className="missed-meta">
                  <span className="missed-realm">{realm?.name || e.realm}</span>
                  <span className="missed-round">Q{e.roundIndex}</span>
                </div>
                <div className="missed-question">{e.question}</div>
                <div className="missed-answer">
                  <span className="missed-answer-letter">{LETTERS[e.correctIndex] ?? ''}</span>
                  <span className="missed-answer-text">{e.choices[e.correctIndex]}</span>
                </div>
                <div className="missed-mine">
                  {e.myChoice === null
                    ? 'You ran out of time.'
                    : <>You picked <strong>{LETTERS[e.myChoice]}</strong>, {e.choices[e.myChoice]}.</>}
                </div>
                {e.explanation && <div className="missed-why">{e.explanation}</div>}
                <div className="missed-report">
                  {state === 'sent' ? (
                    <span className="missed-report-done">Reported. Thank you.</span>
                  ) : state === 'failed' ? (
                    <span className="missed-report-done">Could not send the report.</span>
                  ) : reporting === e.questionId ? (
                    <div className="missed-report-reasons">
                      {REASONS.map((r) => (
                        <button key={r.id} className="missed-reason" onClick={() => report(e.questionId, r.id)}>{r.label}</button>
                      ))}
                      <button className="missed-reason missed-reason-cancel" onClick={() => setReporting(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="missed-report-btn" onClick={() => setReporting(e.questionId)}>Something wrong with this question?</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
